/**
 * OAuthPopupComplete — landing page for the OAuth-popup flow used by the
 * Pulse Decisions Queue's `IntegrationConnectionRow` (Task 7 of the
 * integration-first pivot).
 *
 * The popup lands here after the OAuth callback runs server-side, posts a
 * `yagni:oauth:complete` message back to its `window.opener`, and closes
 * itself. The opener (Pulse) listens for this message and removes the
 * "Connect <source>" row optimistically while the connector framework's
 * syncRunner fires forceRefresh and the briefing fills in.
 *
 * Origin safety: postMessage targets `window.location.origin` so the
 * payload only reaches a same-origin opener. The DecisionRow listener
 * also verifies `ev.origin === window.location.origin` on receive, so a
 * cross-site popup cannot trigger the success handler.
 *
 * Design note: this page renders a minimal status string so the popup is
 * not a blank flash before it closes. Default-on behavior is "post and
 * close immediately"; we tolerate up to ~1s in case the opener is slow.
 */

import { useEffect } from "react";

interface OAuthPopupCompleteProps {
  /** Override `window.close` for tests. */
  closeFn?: () => void;
  /** Override the postMessage target — tests pass a recorder. */
  postMessageFn?: (
    message: unknown,
    targetOrigin: string,
  ) => void;
}

export function OAuthPopupComplete({
  closeFn,
  postMessageFn,
}: OAuthPopupCompleteProps = {}) {
  const params = typeof window === "undefined"
    ? new URLSearchParams()
    : new URLSearchParams(window.location.search);
  const connectorId = params.get("connector") ?? undefined;
  const rawError = params.get("error") ?? undefined;
  // Only forward stable, user-safe codes that the app explicitly understands.
  // Provider error descriptions and arbitrary return_to query values must not
  // cross the postMessage boundary.
  const error = rawError === "workspace_required" ? rawError : undefined;
  const status = rawError || params.get("status") === "error" ? "error" : "connected";
  const failed = status === "error";
  const workspaceRequired = error === "workspace_required";

  useEffect(() => {
    if (typeof window === "undefined") return;

    const send =
      postMessageFn ??
      ((message: unknown, targetOrigin: string) => {
        window.opener?.postMessage(message, targetOrigin);
      });

    send(
      {
        type: "yagni:oauth:complete",
        connectorId,
        status,
        ...(error ? { error } : {}),
      },
      window.location.origin,
    );

    // Keep failures visible so the user can read what happened. Successful
    // callbacks still close automatically after giving the opener a moment to
    // receive the completion message.
    if (failed) return;

    const close = closeFn ?? (() => window.close());
    const timer = window.setTimeout(close, 200);
    return () => window.clearTimeout(timer);
  }, [closeFn, connectorId, error, failed, postMessageFn, status]);

  const close = () => (closeFn ?? (() => window.close()))();

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--canvas-field)",
        fontFamily: "var(--font-display)",
        padding: 24,
      }}
    >
      {failed ? (
        <div
          role="alert"
          style={{
            width: "100%",
            maxWidth: 440,
            padding: 24,
            border: "1px solid var(--error)",
            borderRadius: 12,
            background: "var(--print-bg-solid)",
            boxShadow: "var(--shadow-print)",
          }}
        >
          <h1
            style={{
              margin: 0,
              color: "var(--text-primary)",
              fontSize: "var(--text-xl)",
              fontWeight: 600,
            }}
          >
            {workspaceRequired
              ? "Google Workspace account required"
              : "Connection didn't complete"}
          </h1>
          <p
            style={{
              margin: "12px 0 0",
              color: "var(--text-secondary)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-sm)",
              lineHeight: 1.6,
            }}
          >
            {workspaceRequired
              ? "Personal @gmail.com accounts aren't supported yet. Use a work or school Google account managed through Google Workspace, then try connecting again."
              : "We couldn't finish connecting this account. Close this window and try again."}
          </p>
          <button
            type="button"
            onClick={close}
            style={{
              marginTop: 20,
              border: 0,
              borderRadius: 8,
              padding: "10px 16px",
              background: "var(--accent-warm)",
              color: "var(--text-inverse)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-sm)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close and try again
          </button>
        </div>
      ) : (
        <p
          style={{
            margin: 0,
            color: "var(--text-secondary)",
            fontSize: "var(--text-sm)",
          }}
        >
          Connection complete. You can close this window.
        </p>
      )}
    </div>
  );
}
